import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Drawer, Form, Input, Select, Spin } from "antd";
import { debounce } from "lodash";
import { DatePicker, useJalaliLocaleListener } from "antd-jalali";
import PropTypes from "prop-types";
import Moment from "moment-jalaali";
import dayjs from "dayjs";

import { Modals } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";
import Rules from "scripts/rules";
import { ObjectNumbersToEnglish } from "scripts/helpers";

const Component = ({ data = {}, onUpdate }) => {
	const initialState = { users: [] };
	const [state, setState] = useState(initialState);
	const { loading, submitting, visible, users, usersLoading } = state;
	const { id } = data;
	const { hash, pathname, search } = useLocation();
	const navigate = useNavigate();
	const [form] = Form.useForm();

	const handleSearch = debounce((search) => {
		setState((prevState) => ({ ...prevState, usersLoading: true }));
		if (search?.length > 1) {
			API.user
				.list({ paginate: false, search })
				.then(({ data }) => {
					setState((prevState) => ({
						...prevState,
						users: data.map((o) => ({
							value: o.id,
							label: `${o.firstName} ${o.lastName}`,
						})),
						usersLoading: false,
					}));
				})
				.catch(() => {});
		}
	}, 1000);

	const handleSubmit = () => {
		if (!submitting) {
			form.validateFields().then((values) => {
				setState((prevState) => ({ ...prevState, submitting: true }));

				values = ObjectNumbersToEnglish(values);

				if (values.expireAt)
					values.expireAt = Moment(values.expireAt.$d).format(
						process.env.REACT_APP_DATE_FORMAT
					);

				if (!values.users || values.users?.length === 0) {
					delete values.users;
					values.general = true;
				}
				if (values.users?.length > 0) {
					values.general = false;
				}

				const action =
					hash === `#${Modals.CREATE}`
						? API.notifications.create(values)
						: API.notifications.update(id, values);

				action
					.then(() => {
						onUpdate(null, true);
						navigate(-1);
					})
					.catch(() => {
						setState((prevState) => ({
							...prevState,
							submitting: false,
						}));
					})
					.finally(() => {
						setState((prevState) => ({
							...prevState,
							submitting: false,
						}));
					});
			});
		}
	};

	const componentDidUpdate = () => {
		API.user.list({ pageSize: 50 }).then(({ data: _users }) =>
			setState((prevState) => ({
				...prevState,
				users: _users.map(({ id, fullName }) => ({
					value: id,
					label: fullName,
				})),
			}))
		);
		switch (hash) {
			case `#${Modals.CREATE}`: {
				setState((prevState) => ({ ...prevState, visible: true }));

				break;
			}
			case `#${Modals.EDIT}`: {
				if (id) {
					setState((prevState) => ({
						...prevState,
						visible: true,
						loading: true,
					}));

					API.notifications
						.details(id)
						.then(({ data }) => {
							form.setFieldsValue({
								...data,
								expireAt: dayjs(data.expireAt),
								users: data.users.map(({ id, fullName }) => ({
									value: id,
									label: fullName || "-",
								})),
							});

							setState((prevState) => ({
								...prevState,
								loading: false,
								users: [{ value: data.user?.id, label: data.user?.fullName }],
							}));
						})
						.catch(() => {
							setState((prevState) => ({
								...prevState,
								loading: false,
							}));
						});
				} else {
					navigate(`${pathname}${search}`, { replace: true });
				}

				break;
			}
			default: {
				if (visible) form.resetFields();

				setState(initialState);

				break;
			}
		}
	};

	useEffect(componentDidUpdate, [hash]);

	useJalaliLocaleListener();

	return (
		<Drawer
			className="sp-form-drawer"
			footer={
				<>
					<Button
						type="primary"
						onClick={handleSubmit}
						loading={submitting}
					>
						{Resources.confirm}
					</Button>
					<Button
						onClick={() => navigate(-1)}
						loading={submitting}
					>
						{Resources.cancel}
					</Button>
				</>
			}
			onClose={() => navigate(-1)}
			title={`${
				hash === `#${Modals.CREATE}` ? Resources.add : Resources.edit
			} ${Resources.notification}`}
			maskClosable={false}
			open={visible}
			width={414}
		>
			<Form
				layout="vertical"
				form={form}
			>
				{loading ? (
					<Spin className="sp-drawer-spin" />
				) : (
					<>
						<Form.Item
							name="title"
							label={Resources.title}
							rules={Rules.required}
						>
							<Input />
						</Form.Item>
						<Form.Item
							name="body"
							label={Resources.text}
							rules={Rules.required}
						>
							<Input.TextArea rows={10} />
						</Form.Item>
						<Form.Item
							name="informType"
							label={`${Resources.type} ${Resources.notification}`}
							rules={Rules.required}
						>
							<Select
								options={[
									{ value: "info", label: Resources.info },
									{ value: "success", label: Resources.successfully },
									{ value: "warning", label: Resources.warning },
									{ value: "error", label: Resources.error },
								]}
							/>
						</Form.Item>
						<Form.Item
							name="users"
							label={Resources.user}
						>
							<Select
								options={users}
								onSearch={handleSearch}
								optionFilterProp="label"
								filterOption={false}
								loading={usersLoading}
								showArrow={false}
								showSearch
								mode="multiple"
							/>
						</Form.Item>
					</>
				)}
			</Form>
		</Drawer>
	);
};

Component.propTypes = {
	data: PropTypes.object,
	onUpdate: PropTypes.func.isRequired,
};

export default Component;
